import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import PropTypes from 'prop-types';

import useDataSource from "../../../hooks/useDataSource";

import {
    getFieldAlias
} from "../../../utilities/DataSourceUtilities";

import {
    dataAddFilter,
    dataRemoveFilter,
    filterUpdateConditionAction
} from "../../../redux/data/dataActions";

import {
    styled,
    InputLabel,
    MenuItem,
    FormHelperText,
    FormControl,
    Select,
    LinearProgress,
} from '@mui/material';

const PREFIX = 'DropDownFilter';

const classes = {
    root: `${PREFIX}-root`,
    formControl: `${PREFIX}-formControl`,
    selectEmpty: `${PREFIX}-selectEmpty`,
    linearProgress: `${PREFIX}-linearProgress`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: "100%",
        width: "100%",
        overflow: "hidden"
    },

    [`& .${classes.formControl}`]: {
        margin: 8,
        minWidth: 120,
        width: 'calc(100% - 16px)'
    },

    [`& .${classes.selectEmpty}`]: {
        marginTop: theme.spacing(2),
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    }
}));

export default function DropDownFilter({id, configuration}) {

    const dispatch = useDispatch();

    const removeFilterOnRouteChange = configuration.element?.removeFilterOnRouteChange !== undefined ? configuration.element.removeFilterOnRouteChange : true;
    const hiddenFilter = configuration.element?.hiddenFilter !== undefined ? configuration.element.hiddenFilter : true;
    const helperText = configuration.element?.helperText ? configuration.element.helperText : '';
    const dataSourceName = configuration.dataSource?.name ? configuration.dataSource?.name : '';
    const filterField = configuration.xFilter?.field ? configuration.xFilter?.field : '';

    // App State
    const dataSource = useDataSource(dataSourceName);
    const filters = useSelector(state => state.data.filters);

    // Component State
    const [menuComponents, setMenuComponents] = useState(null);
    const [cachedData, setCachedData] = useState(null);
    // Here we track the selected menu item (data position) and the value that corresponds to the selection
    // in order to update addDataFilter
    const [menuValue, setMenuValue] = useState(-2112);        // Nothing selected by default
    const [filterValue, setFilterValue] = useState(null);   // We read in the existing value or set to -2112 if nothing - this is required bc the setFilterValue does not update before the next hook fires
    const [filterFieldAlias, setFilterFieldAlias] = useState(null);

    useEffect(() => {
        // console.log('DropDownFilter: useEffect([])');

        // Sync to changes from other components i.e.
        // set our internal filter value
        const componentFilters = filters.find(f => {
            return f.id.toLowerCase() === id.toLowerCase()
        });

        if (componentFilters) {
            // TODO: Add utility or store in state since this
            // only assumes single condition - for now we'll use
            // local context however sharing across components
            // TBD will share same f.id
            // HACK:
            let eqCondition = componentFilters.conditions[0];
            eqCondition = eqCondition.substr(eqCondition.indexOf("eq '") + 4);
            eqCondition = eqCondition.substr(0, eqCondition.length - 1);
            eqCondition = eqCondition.toString().toLowerCase();

            // console.log('**');
            // console.log('**');
            // console.log('**');
            // console.log('**');
            // console.log('DropDownFilter: current selected filter value = ' + eqCondition);
            setFilterValue(eqCondition);
        } else {
            // console.log('DropDownFilter: setting filter value to -2112');
            setFilterValue(-2112);
        }

        // Remove filter when user navigates away?
        if (removeFilterOnRouteChange) {
            return function cleanup() {
                const filter = '{0}-{1}'.format(dataSourceName.toLowerCase(), filterField.toLowerCase())
                dispatch(dataRemoveFilter(filter));
            };
        }

    }, []);

    useEffect(() => {
        // console.log('DropDownFilter: useEffect([dataSource.data])');
        // console.log(dataSource, filterValue);

        // Menu's are responsive to filters i.e.
        // a change in Action Plan
        if (dataSource.data && dataSource.fetched && filterValue) {
            if (!filterFieldAlias) {
                const label = getFieldAlias(dataSource, filterField);
                const fieldAlias = label !== null ? label : filterField;
                setFilterFieldAlias(fieldAlias)
            }

            // Setup base data as filtered results or a
            // baseline if included
            let data = dataSource.data;

            // console.log(dataSource.baselines);

            // Find my baseline
            const baseline = dataSource.baselines.find(d => {
                return d.id === id
            });

            if (baseline) {
                // console.log('there is a baseline - looking for ' + filterValue.toString().toLowerCase());
                let useBaseline = true;

                // If there is a baseline then we use
                // that value IF we can highlight
                if (filterValue !== -2112) {
                    if (!baseline.result.find(d => {
                        return d[filterField].toString().toLowerCase() === filterValue.toString().toLowerCase()
                    })) {
                        useBaseline = false;
                    }
                }

                if (useBaseline) {
                    // console.log('we found the baseline to use');
                    data = baseline.result;
                }
            }
            setCachedData(data);

            let firstFilterValue = null;
            const mcs = [];
            for (let d = 0; d < data.length; d++) {
                const dataItem = data[d];
                if (!firstFilterValue)
                    firstFilterValue = dataItem[filterField];

                mcs.push(<MenuItem key={d} value={d}>{dataItem[filterField]}</MenuItem>);
            }

            setMenuComponents(mcs);

            // Select menu
            const componentFilters = filters.find(f => {
                return f.id.toLowerCase() === id.toLowerCase() && f.field.toLowerCase() === filterField.toLowerCase();
            });

            let menuValueSet = false;
            if (componentFilters) {
                // TODO: Add utility or store in state since this
                // only assumes single condition - for now we'll use
                // local context however sharing across components
                // TBD will share same f.id
                // HACK:
                let eqCondition = componentFilters.conditions[0];
                eqCondition = eqCondition.substr(eqCondition.indexOf("eq '") + 4);
                eqCondition = eqCondition.substr(0, eqCondition.length - 1);
                eqCondition = eqCondition.toString().toLowerCase();

                // console.log('"{0}" === "{1}"'.format(filterValue, eqCondition));
                if (filterValue !== eqCondition) {
                    // We're out of sync so we need to update the menu and the filter value
                    // console.log(data);

                    // Set the menu value to the position of eqCondition
                    for (let i = 0; i < data.length; i++) {
                        const d = data[i];
                        // console.log(d[filterField].toString().toLowerCase());
                        if (d[filterField].toString().toLowerCase() === eqCondition) {
                            menuValueSet = true;
                            setMenuValue(i);
                            // console.log('DropDownFilter: setting filter value to ' + eqCondition);
                            setFilterValue(eqCondition);
                        }
                    }
                } else {
                    menuValueSet = syncMenuWithFilterValue(data);
                }
            }

            // Auto-select first menu item
            if (firstFilterValue && !menuValueSet) {
                dispatch(dataAddFilter(id, filterField, " eq '{0}'".format(firstFilterValue), filterUpdateConditionAction.REPLACE, true, hiddenFilter));
                setMenuValue(0);
                // console.log('DropDownFilter: selecting first filter value to ' + firstFilterValue.toString().toLowerCase());
                setFilterValue(firstFilterValue.toString().toLowerCase());
            }
        }

    }, [dataSource.data, dataSource.fetching, dataSource.fetched, dataSource.error, filterValue]);

    useEffect(() => {

        // Sync to changes from other components
        const componentFilters = filters.find(f => {
            return f.id.toLowerCase() === id.toLowerCase()
        });

        if (componentFilters) {
            // TODO: Add utility or store in state since this
            // only assumes single condition - for now we'll use
            // local context however sharing across components
            // TBD will share same f.id
            // HACK:
            let eqCondition = componentFilters.conditions[0];
            eqCondition = eqCondition.substr(eqCondition.indexOf("eq '") + 4);
            eqCondition = eqCondition.substr(0, eqCondition.length - 1);
            eqCondition = eqCondition.toString().toLowerCase();

            setFilterValue(eqCondition);
        } else {
            setFilterValue(-2112);
        }

    }, [filters]);

    const syncMenuWithFilterValue = (data) => {
        for (let i = 0; i < data.length; i++) {
            const d = data[i];
            // console.log(d[filterField].toString().toLowerCase());
            if (d[filterField].toString().toLowerCase() === filterValue.toString().toLowerCase()) {
                setMenuValue(i);
                return true;
            }
        }
        return false;
    }

    const handleChange = (event) => {

        // console.log(cachedData);
        // console.log(event.target.value);

        // Check type because a value of 0 as the place in the array is considered a number
        if (typeof event.target.value === 'number') {
            setMenuValue(event.target.value);
            setFilterValue(cachedData[event.target.value][filterField]);
            dispatch(dataAddFilter(id, filterField, " eq '{0}'".format(cachedData[event.target.value][filterField]), filterUpdateConditionAction.REPLACE, true, hiddenFilter));
        }
    };

    // console.log('menuValue: {0}'.format(menuValue));
    // console.log('DropDownFilter:filterValue: {0}'.format(filterValue));

    return (
        <Root className={classes.root}>
            {!menuComponents && menuValue === -2112 &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {menuComponents &&
                <React.Fragment>
                    <FormControl className={classes.formControl}>
                        <InputLabel variant="standard" id="demo-simple-select-helper-label">{filterFieldAlias}</InputLabel>
                        <Select
                            labelId="demo-simple-select-helper-label"
                            id="demo-simple-select-helper"
                            value={menuValue}
                            onChange={handleChange}
                            variant="standard"
                        >
                            {/*<MenuItem value="">*/}
                            {/*    <em>None</em>*/}
                            {/*</MenuItem>*/}
                            {menuComponents}
                        </Select>
                        <FormHelperText variant="standard">{helperText}</FormHelperText>
                    </FormControl>
                </React.Fragment>
            }
        </Root>
    );
}
